import { lazy, ComponentType } from 'react';

type ModuleFactory<T extends ComponentType<unknown>> = () => Promise<{ default: T }>;

/**
 * Matches the runtime errors browsers throw when a hashed JS chunk referenced by
 * the already-loaded bundle no longer exists on the server — the classic Vite
 * "stale chunk" situation that happens after a new deploy invalidates bundle
 * hashes while a tab is still open.
 */
const CHUNK_LOAD_ERROR =
  /Failed to fetch dynamically imported module|Loading chunk [^ ]+ failed|error loading dynamically imported module|Importing a module script failed/i;

const RELOAD_FLAG = 'bonum:chunk-reload';

/**
 * Drop-in replacement for React.lazy that survives post-deploy stale-chunk 404s.
 *
 * On a chunk-load failure it forces a single full reload (guarded by a
 * sessionStorage flag so it can never loop), which re-fetches the fresh
 * index.html and its new chunk hashes. If the failure is not a chunk-load error,
 * or a reload already happened and it still fails, the error is rethrown so the
 * nearest ErrorBoundary can show a recoverable fallback instead of a blank page.
 */
function lazyWithRetry<T extends ComponentType<unknown>>(factory: ModuleFactory<T>) {
  return lazy<T>(async () => {
    try {
      const module = await factory();
      window.sessionStorage.removeItem(RELOAD_FLAG);
      return module;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const alreadyReloaded = window.sessionStorage.getItem(RELOAD_FLAG);

      if (CHUNK_LOAD_ERROR.test(message) && !alreadyReloaded) {
        window.sessionStorage.setItem(RELOAD_FLAG, '1');
        window.location.reload();
        // Keep Suspense in its fallback until the reload takes over.
        return new Promise<{ default: T }>(() => {});
      }

      throw error;
    }
  });
}

export default lazyWithRetry;
